import React from 'react';
import { ListChecks, BarChart3, ShieldAlert, CheckCircle2, Info, Search } from 'lucide-react';

/**
 * COMPONENTE: ResumoIndiceProvas
 * FINALIDADE: Consolidação de conformidade e índices de precisão.
 * ESTILO: Lead Gray (Bordas) e Branco (Fundo).
 * CORREÇÃO APLICADA: Mapeamento do array 'fotos' para exibição das evidências.
 */
export default function ResumoIndiceProvas({ laudo, statusGeral }) {
  
  // Índices Simulados baseados na NBR 14653
  const indices = [
    { label: "Grau de Fundamentação", valor: "III", status: "Excelente" },
    { label: "Grau de Precisão", valor: "II", status: "Normal" },
    { label: "Saneamento de Dados", valor: "100%", status: "Concluído" }
  ];

  // Extração segura das fotos salvas no JSON (Base64)
  const evidenciasFotograficas = laudo?.fotos || [];

  return (
    <div className="bg-white rounded-[4rem] border-[12px] border-[#2F353B] p-12 space-y-10 relative overflow-hidden">
      {/* Marca d'água técnica de fundo */}
      <div className="absolute top-10 right-10 opacity-[0.05] text-[#2F353B]">
        <BarChart3 size={200} />
      </div>

      <header className="border-b-4 border-[#2F353B] pb-6 flex justify-between items-end">
        <div>
          <h2 className="text-[#2F353B] text-[28px] font-black italic uppercase tracking-tighter">Índices de Provas e Precisão</h2>
          <p className="text-blue-600 font-black text-[11px] uppercase tracking-widest mt-2 italic">Certificação Estatística NBR 14653</p>
        </div>
        <div className="text-right">
          <span className="text-[10px] font-black text-slate-400 block uppercase">Protocolo Ativo</span>
          <span className="text-[#2F353B] font-black text-[18px]">#{laudo?.report_id || "2026-PGLP"}</span>
        </div>
      </header>

      {/* Grid de Indicadores de Desempenho */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
        {indices.map((item, idx) => (
          <div key={idx} className="bg-slate-50 border-2 border-slate-100 p-8 rounded-[3rem] shadow-sm flex flex-col items-center text-center">
            <span className="text-[10px] font-black text-slate-500 uppercase mb-2">{item.label}</span>
            <span className="text-[#2F353B] text-[42px] font-black italic">{item.valor}</span>
            <span className="mt-4 px-6 py-2 bg-[#2F353B] text-white text-[9px] font-black rounded-full uppercase italic">
              {item.status}
            </span>
          </div>
        ))}
      </div>

      {/* SEÇÃO DE EVIDÊNCIAS FOTOGRÁFICAS - Implementada para Visualização e Lupa */}
      <section className="relative z-10">
        <h4 className="text-[#2F353B] font-black text-[14px] uppercase flex items-center gap-3 italic mb-6">
          <Search size={20} className="text-blue-600" /> Álbum de Evidências (Lupa Ativa)
        </h4>
        
        {evidenciasFotograficas.length > 0 ? (
          <div className="grid grid-cols-4 gap-4">
            {evidenciasFotograficas.map((foto, index) => (
              <div key={index} className="relative group h-32 rounded-3xl overflow-hidden border-4 border-slate-100 bg-slate-200 shadow-inner">
                <img 
                  src={foto} 
                  alt={`Evidência ${index}`} 
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-[#2F353B]/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                  <Search className="text-white animate-pulse" size={24} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 border-4 border-dashed border-slate-100 rounded-[2.5rem] text-center">
            <p className="text-[10px] font-black text-slate-400 uppercase italic">Aguardando sincronização de mídias do servidor...</p>
          </div>
        )}
      </section>

      {/* Checklist de Conformidade Normativa */}
      <section className="bg-slate-50 p-10 rounded-[3.5rem] space-y-6 relative z-10">
        <h4 className="text-[#2F353B] font-black text-[14px] uppercase flex items-center gap-3 italic">
          <ListChecks size={20} className="text-blue-600" /> Verificação Pericial Obrigatória
        </h4>
        
        <div className="grid grid-cols-2 gap-4">
          {[
            "Identificação do Imóvel (Geolocalização)",
            "Caracterização da Região (06 Fatores)",
            "Evidências Fotográficas (Marca d'água)",
            "Citação de Fórmulas ($$V_h = V_b \\times (1 + i)$$)"
          ].map((check, i) => (
            <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200">
              <CheckCircle2 size={18} className="text-green-600 shrink-0" />
              <span className="text-[11px] font-black text-[#2F353B] uppercase">{check}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Alerta de Integridade (Regra Absoluta) */}
      <footer className="flex items-center gap-6 p-6 bg-blue-50 border-l-[15px] border-blue-600 rounded-r-[2rem] relative z-10">
        <ShieldAlert size={40} className="text-blue-600" />
        <div>
          <p className="text-[11px] font-black text-[#2F353B] uppercase italic">Declaração de Integridade</p>
          <p className="text-[9px] text-blue-800 font-bold uppercase leading-tight">
            Os dados acima foram processados automaticamente e sincronizados conforme diretriz de segurança MONTE HERMOM e NBR 14653-2:2011.
          </p>
        </div>
      </footer>
    </div>
  );
}